/**
 * The order sign-off popup's bridge: what Medication Administration, Current Medications, Current Procedures and the
 * Orders page read to open `MedadminshortOrderTemplate`, and what that popup posts back (main #339, #349).
 *
 * keyon's `LookupOrderInfoSigSignOff` answered ONE row: the order with its two signatures and their timestamps, joined
 * to the medication the popup was opened for and to the patient it is for. The port keeps those in four places - the
 * order and its signatures (`GET /orders/{o}`), the signature context (`GET /orders/{o}/signature-context`), the
 * medication and the patient (`GET /patients/{p}`) - and the table used to pass the context through unchanged. The
 * popup then had no order number, no medication, no signatures and no timestamps: it routed its sign-off to
 * /orders/null/signatures, and `applySignature` always ended in "Unable to apply this signature for your account type"
 * (NUR339-11, MERGE-VERIFY).
 */

import api, { ApiError } from './client';
import {
  id, okStatus, orderRef, routeId, toLocalYMD, waitingOrderOf,
} from './bridgeShapes';
import { orderDetailRow } from './clinicalBridge';

/**
 * The account types whose signature a pending order waits on, in main's words ("Registered Nurse") or the bridge's
 * slug ("registered_nurse" - HomeGrid sends either). Main's PendingOrderFilter knows exactly these three; every other
 * type is counted by status alone.
 */
export function isSigningAccountType(accountType) {
  return /^(provider|registered[ _]nurse|licensed[ _]practical[ _]nurse)$/i.test(String(accountType || '').trim());
}

/**
 * The query that picks the orders waiting on the person asking.
 *
 * A provider, a registered nurse or a licensed practical nurse asks the server for the orders waiting on THEIR
 * signature (`filter[awaiting_my_signature]`): the server decides which half is theirs from the token, not from the
 * account type sent here, which only chooses between the two queries main chose between. Anyone else is counted by
 * status, as main counts them (`$otherStatuses`).
 */
export function pendingOrdersFilter(accountType, otherStatuses) {
  return isSigningAccountType(accountType)
    ? { 'filter[awaiting_my_signature]': 1 }
    : { 'filter[status]': otherStatuses };
}

/** What the lookup answers: keyon's status/code pair and the rows (empty when no order is in view). */
const lookupAnswer = (rows) => okStatus('200 Successfull', { code: '200 Successfull', results: rows });

/** The text of a signature, out of the popup's provider object ({signature, signedDate, ...}) or a plain string. */
const signatureText = (value) => {
  if (value && typeof value === 'object') return String(value.signature || '').trim();

  return String(value || '').trim();
};

/** "hold" -> "Hold": main's `medchangetype` spelling, which the popup compares with 'Hold'. */
const capitalised = (word) => {
  const text = String(word || '');

  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

/**
 * `medchangetype` as the popup reads it (`checkHoldOrderStatus`: "Hold" labels the Not Approve dialog a hold's, any
 * other word a discontinue's). A hold order and a discontinue order say what they are on the order; the medication
 * it was opened for says it otherwise.
 */
function changeTypeOf(order, line) {
  const orderType = String(order.change_type || '');

  if (['hold', 'discontinue', 'resume', 'change'].includes(orderType)) return capitalised(orderType);
  if (['expire', 'not_approved'].includes(orderType)) return 'Discontinue';

  return capitalised(line && line.change_type) || capitalised(orderType);
}

/** A value as the text the popup prints: no null, no padding. */
const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

/**
 * The patient's part of main's row, in main's spellings (`patients.first_name AS patient_firstname` and its fellows):
 * the popup's heading and DOB line, and the address, phone, email and emergency contact under them. The emergency
 * contact is the patient's contact flagged as one (main kept it in two columns of the patient).
 *
 * Nothing at all when the patient could not be read: the popup then shows no identity, as it would for a row main
 * could not join, and the order is still there to be signed.
 *
 * @param {object|null} patient  `GET /patients/{p}?include=contacts`
 */
export function patientBlock(patient) {
  if (!patient) return {};

  const contacts = Array.isArray(patient.contacts) ? patient.contacts : [];
  const emergency = contacts.find((contact) => contact && contact.is_emergency_contact) || null;

  return {
    patient_firstname: text(patient.first_name),
    patient_lastname: text(patient.last_name),
    patient_dob: text(patient.date_of_birth),
    patient_address: [patient.address_line1, patient.address_line2].map(text).filter(Boolean).join(' '),
    patient_city: text(patient.city),
    patient_state: text(patient.state),
    patient_zip: text(patient.postal_code),
    patient_phone: text(patient.phone),
    patient_email: text(patient.email),
    emergency_contactname: emergency
      ? (text(emergency.full_name) || [emergency.first_name, emergency.last_name].map(text).filter(Boolean).join(' '))
      : '',
    emergency_contact_number: emergency ? text(emergency.phone) : '',
  };
}

/**
 * The popup's single row.
 *
 * `orderRows` (bridgeMap.js) is the shaper every order grid goes through: it keeps the routing key (`order_number` is
 * the id the routes bind), the order's description, the prescriber's name and both signatures' names, and says the
 * order's status in main's word. That is `order_status`, which the popup prints as its badge - 'Active-Pending', and
 * 'Hold' for a hold order, as main's `orders.status AS order_status` did. This adds what only the popup reads: the
 * medication it was opened for, the prescriber's contact block, and each signature's time as the wall clock in the
 * organization's zone - "YYYY-MM-DD HH:MM:SS", which `formatSignatureTime` shows as stored and never converts. The API
 * sends that reading beside the instant (`signed_local`); this does not convert in the browser's zone.
 *
 * @param {Function} orderRows  bridgeMap's order-row shaper
 * @param {object}   order      `GET /orders/{o}?include=medications,signatures,subject_medication`
 * @param {object}   context    `GET /orders/{o}/signature-context`
 * @param {object}   p          the legacy payload (medentryid, type)
 * @param {object}   [fetched]  the medication the popup was opened for, read on its own: the line printed when the order
 *                              carries none of its own
 * @param {object}   [patient]  the patient the order is for, with their contacts
 */
export function signOffRow(orderRows, order, context, p, fetched = null, patient = null) {
  const base = orderRows([order])[0];
  const lines = Array.isArray(order.medications) ? order.medications : [];
  const wanted = id(p.medentryid);
  // The medication the popup was opened for, else the order's own line, else the medication a hold, discontinue or
  // release order was raised for. Main copied the medication row onto those orders (CopyMedicationRow), so its popup
  // always had a line to print; the port's carry none (`medications` is empty), and the Orders page names no
  // medication when it opens them, so the API names it (`?include=subject_medication`).
  const line = (wanted !== null && lines.find((m) => Number(m.id) === wanted))
    || fetched
    || lines[0]
    || order.subject_medication
    || null;
  const items = context && Array.isArray(context.items) ? context.items : [];
  const item = items.find((i) => wanted !== null && Number(i.id) === wanted) || items[0] || null;
  const sig = order.signatures_summary || {};
  const prescriber = order.prescriber || {};
  // A procedure's popup reads the procedure's own columns (procedure_code, instruction, diagnosis_type) where a
  // medication's reads the line's; the order's half is the same.
  const procedure = String(p.type || '').toLowerCase() === 'procedure'
    ? { ...orderDetailRow(order, { type: 'procedure', id: p.medentryid }), diagnosis_type: 'Procedure' }
    : null;

  return {
    ...(procedure || {}),
    ...base,
    medentryid: line ? line.id : (wanted ?? (item ? item.id : undefined)),
    item_id: procedure ? procedure.id : (line ? line.id : (item ? item.id : undefined)),
    medname: line ? line.name : (item ? item.label : ''),
    med_amount: line ? line.dose_amount : undefined,
    med_frequency: line ? line.frequency_code : undefined,
    med_doseuom: line ? line.dose_unit : undefined,
    total: line ? line.total_quantity : undefined,
    med_instruction: line ? line.instructions : undefined,
    med_startdate: line ? line.start_on : undefined,
    med_enddate: line ? line.end_on : undefined,
    medchangetype: changeTypeOf(order, line),
    provider_npi: prescriber.npi || (sig.provider && sig.provider.npi) || '',
    provider_email: prescriber.email || '',
    provider_phone: prescriber.phone || '',
    provider_fax: prescriber.fax || '',
    provider_address: prescriber.address_line1 || '',
    // The wall clock of the latest signature of each half; '' while the half is unsigned, which the popup shows as none.
    provsigtimestamp: (sig.provider && sig.provider.signed_local) || '',
    nurssigtimestamp: (sig.nurse && sig.nurse.signed_local) || '',
    ...patientBlock(patient),
  };
}

/**
 * The reads that make one order's side of the popup: the order with its medications and signatures (and, for a hold,
 * discontinue or release order, the medication it was raised for), and its signature context for the item asked about.
 */
function readOrder(orderId, p) {
  const procedure = String(p.type || '').toLowerCase() === 'procedure';

  return Promise.all([
    api.get(`/orders/${orderId}`, { include: procedure ? 'medications,procedures,signatures' : 'medications,signatures,subject_medication' }),
    api.get(`/orders/${orderId}/signature-context`, {
      'filter[type]': p.type || undefined,
      'filter[item_id]': id(p.medentryid) || undefined,
    }),
  ]);
}

/**
 * `MedicationAdmin.LookupOrderInfoSigSignOff`: the order, its signature context, the patient it is for and (when the
 * order carries none of its lines) the medication the popup was opened for, answered as keyon's one row.
 *
 * Several reads rather than one because the port keeps them apart; none writes anything. The popup may be opened with
 * no medication at all: the Orders page opens a hold, release or discontinue order that way, since the order is raised
 * for a medication but carries no line of its own, and its author's half is signed, so the card for the other half is
 * on that page. The medication such an order was raised for comes with the order (`subject_medication`), as main's
 * copied medication row did; the signatures and the patient are read beside it.
 *
 * ## The order opened is the one the row waits on (P-ORD2F O2)
 *
 * The MAR and Current Medications ask for the line's own order (a row's `order_number` is the order the line was
 * prescribed under, which eleven call sites post back), and name the line. A line with a hold a clinician has just
 * placed, or a released course, waits on ANOTHER order, the hold's or the release's (`waitingOrderOf`: the order that
 * greys the row; main's row is a copy under it), and the row's signature card is for that order. The line's own order
 * is signed by both halves and takes no signature, so opening it left the hold that blocks every dose of the line
 * unsignable from the MAR. When the order asked for is the line's own and the line waits on another order, that order
 * is opened in its place and the row answers with ITS number, so the sign-off the popup posts goes to the order it
 * shows.
 *
 * ## Only the line's own order is moved (P-ORD-DOC2G G2)
 *
 * A signature must land on the order the clinician opened, so any other order asked for is opened as asked. The Orders
 * page opens a pending order by its OWN number and names a medication too (the line the order carries, or the one a
 * hold, discontinue or release order was raised for), and a line can have several half-signed orders at once: a
 * discontinue beside a hold, a release while the hold's order still waits, an older hold under a newer one. The line
 * names only one of them (`waitingOrderOf`: the release, else the newest hold), so moving every order that differed
 * from that one opened it in place of the order the clinician clicked and the signature went on the wrong document.
 * The click that is moved is the one that names the line's own order, `order_id` of the medication read.
 *
 * The medication is read beside the order, so the common case (a line waiting on nothing) adds no waiting, and a
 * medication that cannot be read leaves the order asked for, as it was. An order opened as asked (the Orders page
 * opening the hold's own order, say) is read once.
 *
 * @param {object}   p          the legacy payload
 * @param {Function} orderRows  bridgeMap's order-row shaper
 */
export async function lookupOrderSignOff(p, orderRows) {
  const asked = orderRef(p);

  // A read with no order in view is the normal state of a screen that has not been told one, not a failure
  // (legacyBridge's unresolved-path rule): nothing is answered, and the screen's own "no data" branch runs.
  if (asked === null) return lookupAnswer([]);

  // A hold or discontinue order carries no line of its own: the medication it was raised for is the one the popup was
  // opened for, so its name and dose come from that medication (signOffRow). A procedure's popup names a procedure,
  // not a medication.
  const wanted = routeId(p.medentryid);
  const forMedication = wanted !== null && String(p.type || 'medication') === 'medication';
  const [asking, line] = await Promise.all([
    readOrder(asked, p),
    forMedication ? api.get(`/medications/${wanted}`, { include: 'hold_order,release_order' }).catch(() => null) : null,
  ]);

  // Moved only from the line's own order: any other order asked for is the one the clinician clicked, and is signed.
  const ownOrder = routeId(line?.order_id);
  const waiting = ownOrder === asked ? routeId(waitingOrderOf(line)?.id) : null;
  const [order, context] = waiting !== null && waiting !== asked ? await readOrder(waiting, p) : asking;

  if (!order) {
    throw new ApiError({ status: 404, code: 'not_found', message: 'That order could not be found.' });
  }

  // The patient is the order's own, not the payload's: the payload's id is the legacy one the screen holds. Chart
  // access and the audit trail are the patient endpoint's; a signer who may not read the chart still signs the order.
  const patientId = id(order.patient_id);
  const patient = patientId !== null ? await api.get(`/patients/${patientId}`, { include: 'contacts' }).catch(() => null) : null;

  return lookupAnswer([signOffRow(orderRows, order, context, p, line, patient)]);
}

/**
 * The body of `MedicationAdmin.UpdateOrderWithNurseSignature`, per action.
 *
 * The popup emits only when both signatures are on it, so `nurseSignature` is always set and the old
 * `nurseSignature || providerSignature` recorded the nurse's text under a provider who was countersigning, with the
 * nurse's day and no role (MERGE-VERIFY). The action says which half is new:
 *
 *  - 'Update Provider Signature' - a provider countersigning after the nurse: the provider's text (the popup sends its
 *    provider object, `{signature, signedDate, ...}`), the provider's day, and the provider's half named;
 *  - 'Update Nurse Signature' - a nurse signing off a provider's order: the nurse's text and day. The half is the
 *    signer's own, derived by the server from the token.
 *
 * The day is the signer's own calendar day (`toLocalYMD`): the pop-up stamps a signature with the browser's clock and
 * keyon keeps only its date; the UTC day of that time is tomorrow from 20:00 in New York. The server takes the
 * organization's day when the client's is not the organization's today.
 */
export function signOffBody(p) {
  const action = String(p.action || '');
  const providerText = signatureText(p.providerSignature);
  const providerDay = toLocalYMD(p.providerSignedDate || (p.providerSignature && p.providerSignature.signedDate));

  if (/provider/i.test(action)) {
    return {
      signature: providerText || signatureText(p.nurseSignature),
      signed_on: providerDay,
      role_override: 'provider',
    };
  }

  // No day (null) is the server's to date: the organization's today.
  return {
    signature: signatureText(p.nurseSignature) || providerText,
    signed_on: toLocalYMD(p.nurseSigDate) || providerDay,
    role_override: signatureText(p.nurseSignature) ? undefined : 'provider',
  };
}

/**
 * The body of `OrderTemp.UpdateOrder`: the Pending Orders popup's Save, which is a countersignature.
 *
 * The popup pre-fills BOTH signature boxes from the order and posts both (`nurseSignature` + `provSignature`, each with
 * its day), so which half is new cannot be told from the payload. Reading the nurse's first, as this did, filed a
 * provider's countersignature of a nurse's hold, release or change order under the nurse's typed name and the nurse's
 * earlier day - and since a nurse's own hold order now arrives already signed by her, that is the ordinary case, not a
 * rare one. The signer's own half is the one to read, and the signer is the signed-in account (the server derives the
 * half from the same account): a provider's is the provider box and day, a nurse's the nurse box and day. An account
 * type that is neither (or unknown) keeps the older reading, the nurse's first.
 *
 * @param {object} p            the legacy payload
 * @param {string} accountType  the signed-in account's type, 'provider' or a nurse's ('registered_nurse', ...)
 */
export function countersignBody(p, accountType) {
  const nurse = { text: signatureText(p.nurseSignature), day: p.nurseSigDate };
  const provider = {
    text: signatureText(p.provSignature) || signatureText(p.providerSignature),
    day: p.provSigDate || p.providerSignedDate,
  };
  const type = String(accountType || '').toLowerCase();
  const [own, other] = type === 'provider' ? [provider, nurse] : [nurse, provider];

  return {
    signature: own.text || other.text,
    // The signer's own calendar day, not the UTC day of the stamp (from 20:00 in New York that is tomorrow).
    signed_on: toLocalYMD(own.day) || toLocalYMD(other.day),
  };
}
